import { useCallback, useMemo } from "react";
import {
  useLiveNotificationsStore,
  type LiveNotification,
} from "store/liveNotificationsStore";
import { NOTIFICATIONS_EVENT_TYPE } from "store/websocketStore";

export type {
  LiveNotification,
  LiveNotificationType,
} from "store/liveNotificationsStore";

const NO_NOTIFICATIONS: LiveNotification[] = [];

export type UseLiveNotificationsResult = {
  notifications: LiveNotification[];
  /** Number of unread notifications (drives the bell badge). */
  unseenCount: number;
  handlePeak: (projectId: string, maxConcurrent: number) => void;
  handleMilestone: (projectId: string, totalViews: number) => void;
  handleTrending: (projectId: string) => void;
  handleChatbotQuestion: (projectId: string) => void;
  handleQuizSubmitted: (projectId: string, score?: number) => void;
  markRead: (id: string) => void;
  markAllRead: () => void;
};

export function useLiveNotifications(
  organizationId: string | undefined
): UseLiveNotificationsResult {
  const storedOrganizationId = useLiveNotificationsStore(
    (state) => state.organizationId
  );
  const storedNotifications = useLiveNotificationsStore(
    (state) => state.notifications
  );
  const add = useLiveNotificationsStore((state) => state.add);
  const markRead = useLiveNotificationsStore((state) => state.markRead);
  const markAllRead = useLiveNotificationsStore((state) => state.markAllRead);

  // A list kept for another organization is not shown.
  const notifications =
    organizationId && storedOrganizationId === organizationId
      ? storedNotifications
      : NO_NOTIFICATIONS;

  const handlePeak = useCallback(
    (projectId: string, maxConcurrent: number) => {
      if (organizationId)
        add(
          organizationId,
          NOTIFICATIONS_EVENT_TYPE.NewMaxConcurrentViewers,
          projectId,
          maxConcurrent
        );
    },
    [add, organizationId]
  );

  const handleMilestone = useCallback(
    (projectId: string, totalViews: number) => {
      if (organizationId)
        add(
          organizationId,
          NOTIFICATIONS_EVENT_TYPE.ViewMilestoneReached,
          projectId,
          totalViews
        );
    },
    [add, organizationId]
  );

  const handleTrending = useCallback(
    (projectId: string) => {
      if (organizationId)
        add(organizationId, NOTIFICATIONS_EVENT_TYPE.ProjectTrending, projectId);
    },
    [add, organizationId]
  );

  // Each chatbot question is its own event, so no dedupe.
  const handleChatbotQuestion = useCallback(
    (projectId: string) => {
      if (organizationId)
        add(
          organizationId,
          NOTIFICATIONS_EVENT_TYPE.ChatbotMessageAnswered,
          projectId,
          undefined,
          false
        );
    },
    [add, organizationId]
  );

  // Each quiz attempt is its own event, so no dedupe.
  const handleQuizSubmitted = useCallback(
    (projectId: string, score?: number) => {
      if (organizationId)
        add(
          organizationId,
          NOTIFICATIONS_EVENT_TYPE.QuizSubmitted,
          projectId,
          score,
          false
        );
    },
    [add, organizationId]
  );

  const unseenCount = useMemo(
    () => notifications.filter((n) => !n.seen).length,
    [notifications]
  );

  return {
    notifications,
    unseenCount,
    handlePeak,
    handleMilestone,
    handleTrending,
    handleChatbotQuestion,
    handleQuizSubmitted,
    markRead,
    markAllRead,
  };
}
